import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { MsalAuthenticationTemplate } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest, tokenRequest } from '../../authConfig';
import { callMsGraph } from '../../graph';
import { useAuthorization } from '../../hooks/useAuthorization';
import { UnauthorizedAccess } from './UnauthorizedAccess';
import AuthLogger from '../../utils/AuthLogger';
import { AZURE_AD_CONFIG } from '../../config/azureAdConfig';
import { AuthorizationResult } from '../../types/auth';
import { userDbApi } from '../../services/userDbApi';
import { acquireTokenSilentWithRetry } from '../../utils/tokenAcquisitionHelper';

const AUTH_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

interface CachedAuthResult {
  result: AuthorizationResult;
  userEmail: string;
  displayName?: string;
  timestamp: number;
}

interface AuthorizedRouteProps {
  children: React.ReactNode;
}

/**
 * Inner component that handles authorization (whitelist/DB check) with session caching.
 * This only renders when MSAL has confirmed the user is authenticated.
 */
const AuthorizationGate: React.FC<AuthorizedRouteProps> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const { getAuthorizationResult } = useAuthorization();

  const [loading, setLoading] = useState(true);
  const [authResult, setAuthResult] = useState<AuthorizationResult | null>(null);
  const [userEmail, setUserEmail] = useState<string | undefined>();

  const cacheRef = useRef<CachedAuthResult | null>(null);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const checkInFlightRef = useRef(false);

  const isCacheValid = useCallback((): boolean => {
    if (!cacheRef.current) return false;
    const age = Date.now() - cacheRef.current.timestamp;
    if (age > AUTH_CACHE_TTL_MS) return false;
    const currentEmail = account?.username?.toLowerCase();
    if (currentEmail !== cacheRef.current.userEmail.toLowerCase()) return false;
    return true;
  }, [account?.username]);

  useEffect(() => {
    if (!account) {
      setLoading(false);
      return;
    }

    if (isCacheValid()) {
      const cached = cacheRef.current!;
      setAuthResult(cached.result);
      setUserEmail(cached.userEmail);
      setLoading(false);
      return;
    }

    if (checkInFlightRef.current) return;
    checkInFlightRef.current = true;

    const checkAuthorization = async () => {
      try {
        const email = account.username;
        AuthLogger.logAuthorizationCheck(email, account.name);

        const result = await getAuthorizationResult();

        let resolvedDisplayName = account.name;
        let resolvedEmail = result.userEmail || email;

        const shouldCallGraphApi = result.isAuthorized &&
                                   result.authorizationMethod === 'group';

        if (shouldCallGraphApi) {
          try {
            const response = await acquireTokenSilentWithRetry(instance, {
              ...tokenRequest,
              account: account
            });
            if (response) {
              const userProfile = await callMsGraph(response.accessToken);
              resolvedEmail = userProfile.mail || resolvedEmail;
              resolvedDisplayName = userProfile.displayName || resolvedDisplayName;
            }
          } catch (graphError) {
            console.error('Graph API call failed:', graphError);
          }
        }

        if (resolvedEmail) {
          userDbApi.ensureUserExists(resolvedEmail, resolvedDisplayName || undefined);
        }

        if (result.isAuthorized) {
          AuthLogger.logAuthorizationGranted(
            resolvedEmail || 'unknown',
            resolvedDisplayName || 'unknown',
            sessionId,
            result.authorizationMethod === 'none' ? undefined : result.authorizationMethod
          );
          AuthLogger.logLoginSuccess(resolvedEmail || 'unknown', resolvedDisplayName || 'unknown', sessionId);

          if (AZURE_AD_CONFIG.ENABLE_AUTH_LOGGING) {
            console.log('User authorized via', result.authorizationMethod);
          }
        } else {
          AuthLogger.logAuthorizationDenied(
            resolvedEmail || 'unknown',
            resolvedDisplayName || 'unknown',
            result.errorMessage || 'Authorization failed'
          );

          if (AZURE_AD_CONFIG.ENABLE_AUTH_LOGGING) {
            console.log('User not authorized');
          }
        }

        cacheRef.current = {
          result,
          userEmail: resolvedEmail || email,
          displayName: resolvedDisplayName,
          timestamp: Date.now()
        };

        setAuthResult(result);
        setUserEmail(resolvedEmail);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        const email = account.username || 'unknown';

        console.error('Authorization check failed:', error);
        AuthLogger.logError(email, errorMessage, 'authorization_check');

        setAuthResult({
          isAuthorized: false,
          authorizationMethod: 'none',
          groupCheckPerformed: false,
          emailCheckPerformed: true,
          userEmail: email,
          errorMessage
        });
        setUserEmail(email);
      } finally {
        setLoading(false);
        checkInFlightRef.current = false;
      }
    };

    checkAuthorization();
  }, [account, instance, sessionId, getAuthorizationResult, isCacheValid]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#42B0D5] mx-auto mb-4"></div>
          <p className="text-white text-lg">Checking authorization...</p>
          <p className="text-gray-400 text-sm mt-2">Validating access permissions</p>
        </div>
      </div>
    );
  }

  if (!authResult?.isAuthorized) {
    return (
      <UnauthorizedAccess
        userEmail={userEmail}
        authorizationResult={authResult || undefined}
      />
    );
  }

  return <>{children}</>;
};

function AuthLoading() {
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#42B0D5] mx-auto mb-4"></div>
        <p className="text-white text-lg">Authenticating...</p>
      </div>
    </div>
  );
}

function AuthError({ error }: { error: any }) {
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="text-center max-w-md mx-auto px-6">
        <p className="text-white text-lg mb-2">Authentication Error</p>
        <p className="text-gray-400 text-sm mb-4">
          {error?.errorMessage || 'An error occurred during authentication.'}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="bg-[#42B0D5] text-white px-6 py-2 rounded-lg hover:bg-[#3a9dbe] transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  );
}

/**
 * Protects a route by requiring both MSAL authentication and authorization.
 *
 * Authentication: handled by MsalAuthenticationTemplate (auto-triggers redirect login).
 * Authorization: handled by AuthorizationGate (JSON whitelist -> DB whitelist, cached per session).
 */
export const AuthorizedRoute: React.FC<AuthorizedRouteProps> = ({ children }) => {
  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={loginRequest}
      loadingComponent={AuthLoading}
      errorComponent={AuthError}
    >
      <AuthorizationGate>{children}</AuthorizationGate>
    </MsalAuthenticationTemplate>
  );
};

export default AuthorizedRoute;
